import type { AssetContractReference, DexSnapshot } from '../../types/market';
import { safeNumber } from '../../lib/number';
import { fetchJson } from '../exchanges/http';

interface DexPair {
  chainId: string;
  dexId: string;
  pairAddress: string;
  baseToken: { symbol: string; address: string };
  quoteToken: { symbol: string; address: string };
  volume?: { h24?: number };
  liquidity?: { usd?: number };
  priceChange?: { h24?: number };
  txns?: { h24?: { buys?: number; sells?: number } };
}

interface DexSearchResponse {
  pairs: DexPair[] | null;
}

const PLATFORM_TO_DEX_CHAIN: Record<string, string> = {
  ethereum: 'ethereum',
  'binance-smart-chain': 'bsc',
  'polygon-pos': 'polygon',
  'arbitrum-one': 'arbitrum',
  optimism: 'optimism',
  base: 'base',
  solana: 'solana',
  avalanche: 'avalanche',
  fantom: 'fantom',
  tron: 'tron',
  sui: 'sui',
};

function aggregatePairs(pairs: DexPair[], identityConfidence: DexSnapshot['identityConfidence'], source: string): DexSnapshot {
  const unique = new Map<string, DexPair>();
  for (const pair of pairs) unique.set(`${pair.chainId}:${pair.pairAddress}`, pair);
  const ranked = [...unique.values()]
    .sort((left, right) => safeNumber(right.liquidity?.usd) - safeNumber(left.liquidity?.usd))
    .slice(0, 30);
  if (ranked.length === 0) return { pairCount: 0, identityConfidence: 'unavailable', source };

  const volume24h = ranked.reduce((sum, pair) => sum + safeNumber(pair.volume?.h24), 0);
  const liquidityUsd = ranked.reduce((sum, pair) => sum + safeNumber(pair.liquidity?.usd), 0);
  const buys24h = ranked.reduce((sum, pair) => sum + safeNumber(pair.txns?.h24?.buys), 0);
  const sells24h = ranked.reduce((sum, pair) => sum + safeNumber(pair.txns?.h24?.sells), 0);
  const weightedChange = volume24h > 0
    ? ranked.reduce((sum, pair) => sum + safeNumber(pair.priceChange?.h24) * safeNumber(pair.volume?.h24), 0) / volume24h
    : safeNumber(ranked[0]?.priceChange?.h24);

  return {
    volume24h,
    liquidityUsd,
    buys24h,
    sells24h,
    priceChange24h: weightedChange,
    pairCount: ranked.length,
    topPair: `${ranked[0]!.baseToken.symbol}/${ranked[0]!.quoteToken.symbol} · ${ranked[0]!.dexId} · ${ranked[0]!.chainId}`,
    identityConfidence,
    source,
  };
}

async function getVerifiedContractPairs(contracts: AssetContractReference[]): Promise<DexPair[]> {
  const supported = contracts
    .map((contract) => ({ ...contract, chainId: PLATFORM_TO_DEX_CHAIN[contract.platform] }))
    .filter((contract): contract is AssetContractReference & { chainId: string } => Boolean(contract.chainId))
    .slice(0, 5);
  const results = await Promise.allSettled(
    supported.map((contract) =>
      fetchJson<DexPair[]>(
        `https://api.dexscreener.com/token-pairs/v1/${encodeURIComponent(contract.chainId)}/${encodeURIComponent(contract.address)}`,
        7_000,
      ),
    ),
  );
  return results.flatMap((result) => (result.status === 'fulfilled' ? result.value : []));
}

export async function getDexSnapshot(baseAsset: string, contracts: AssetContractReference[] = []): Promise<DexSnapshot> {
  try {
    if (contracts.length > 0) {
      const pairs = await getVerifiedContractPairs(contracts);
      if (pairs.length > 0) {
        const addresses = new Set(contracts.map((contract) => contract.address.toLowerCase()));
        const verifiedPairs = pairs.filter(
          (pair) => addresses.has(pair.baseToken.address.toLowerCase()) || addresses.has(pair.quoteToken.address.toLowerCase()),
        );
        return aggregatePairs(verifiedPairs, 'verified-contract', 'DEX Screener contract-resolved pool data');
      }
    }

    const response = await fetchJson<DexSearchResponse>(
      `https://api.dexscreener.com/latest/dex/search?q=${encodeURIComponent(`${baseAsset} USDT`)}`,
      7_000,
    );
    const pairs = (response.pairs ?? []).filter((pair) => pair.baseToken.symbol.toUpperCase() === baseAsset.toUpperCase());
    const result = aggregatePairs(pairs, 'symbol-only', 'DEX Screener symbol search · identity not contract-verified');
    return result.pairCount > 0 ? result : { pairCount: 0, identityConfidence: 'unavailable', source: 'DEX Screener returned no matching pools' };
  } catch {
    return { pairCount: 0, identityConfidence: 'unavailable', source: 'DEX data unavailable or rate-limited' };
  }
}
